import { logger } from '@/utils/logger';
import {
  isNetworkError,
  markWipePending,
  resetOutboxState,
  wipeOfflineStore,
} from '@shared/offline';

// BEGIN FULL AUTH IMPLEMENTATION
// Detect if we are in development
const isDevelopment =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

// Use current origin for API calls. In dev we go through the Vite dev server
// (5173) so the OAuth redirect stays on one origin; Vite proxies /api/* to the
// uvicorn API on 8000.
const API_URL = window.location.origin;
void isDevelopment;

// Debug info

logger.info('🌍 Environment Detection (auth.ts):');
logger.info('=====================================');
logger.info('- Hostname:', window.location.hostname);
logger.info('- Is Development:', isDevelopment);
logger.info('- API URL:', API_URL);
logger.info('- Full Location:', window.location.href);
logger.info('=====================================');

const TOKEN_REFRESH_THRESHOLD = 5 * 60 * 1000;

const TOKEN_KEY = 'auth_token';
const TOKEN_EXPIRY_KEY = 'auth_token_expiry';
const REMEMBER_ME_KEY = 'auth_remember_me';
const USER_EMAIL_KEY = 'auth_user_email';

const BROWSER_SESSION_KEY = 'auth_browser_session';
const LOGOUT_ON_BROWSER_CLOSE_KEY = 'auth_logout_on_browser_close';

const AUTH_STATE_CHANGE_EVENT = 'authStateChange';
// A same-user token replacing a live one. Not an auth-state change: nothing
// about who is signed in moved, so nothing should reload. The offline flusher
// listens for it by name (it cannot import this module).
const TOKEN_ROTATED_EVENT = 'authTokenRotated';

interface TokenPayload {
  exp: number;
  sub: string;
  [key: string]: unknown;
}

interface AuthStateChangeEvent extends CustomEvent {
  detail: { isAuthenticated: boolean };
}

const notifyAuthStateChange = (isAuthenticated: boolean): void => {
  window.dispatchEvent(new CustomEvent(AUTH_STATE_CHANGE_EVENT, { detail: { isAuthenticated } }));
};

const notifyTokenRotated = (token: string): void => {
  window.dispatchEvent(new CustomEvent(TOKEN_ROTATED_EVENT, { detail: { token } }));
};

/** True when a still-valid token for `sub` is already stored. */
const hasLiveSessionFor = (sub: string): boolean => {
  const expiry = Number(localStorage.getItem(TOKEN_EXPIRY_KEY));
  return (
    !!localStorage.getItem(TOKEN_KEY) &&
    expiry > Date.now() &&
    localStorage.getItem(USER_EMAIL_KEY) === sub
  );
};

export const login = async (rememberMe: boolean = true): Promise<void> => {
  localStorage.setItem(REMEMBER_ME_KEY, String(rememberMe));
  window.location.href = `${API_URL}/api/auth/login`;
};

/**
 * End the authenticated session without touching the offline store.
 *
 * Token expiry, a failed refresh, and a 401 all land here. Queued offline
 * work must survive these so it can replay after the user signs in again.
 */
export const endSession = (): void => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(TOKEN_EXPIRY_KEY);
  localStorage.removeItem(USER_EMAIL_KEY);
  localStorage.removeItem(REMEMBER_ME_KEY);
  // The queued rows survive, but their counts belong to the session that made
  // them: a signed-out shell must not keep showing another account's total.
  resetOutboxState();
  notifyAuthStateChange(false);
};

const WIPE_DEADLINE_MS = 2_000;

/**
 * User-initiated logout: end the session and wipe this device's offline copy.
 *
 * The wipe is awaited, bounded, before the redirect unloads the page; a wipe
 * that overruns the deadline — or fails outright — finishes on the next boot
 * via the flag, so neither can strand a signed-in session. The wipe runs
 * before `endSession` because it reads the account id `endSession` clears.
 */
export const logout = async (): Promise<void> => {
  const wiped = wipeOfflineStore().then(
    () => true,
    () => false
  );
  const deadline = new Promise<false>(resolve =>
    setTimeout(() => resolve(false), WIPE_DEADLINE_MS)
  );
  if (!(await Promise.race([wiped, deadline]))) markWipePending();
  endSession();
  window.location.href = `${API_URL}/api/auth/logout`;
};

export const getToken = (): string | null => {
  logger.info('🔍 getToken() called');

  try {
    const token = localStorage.getItem(TOKEN_KEY);
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !expiry) {
      return null;
    }

    const now = Date.now();
    const expiryTime = Number(expiry);

    if (Number.isNaN(expiryTime) || now >= expiryTime) {
      removeToken();
      return null;
    }

    if (now + TOKEN_REFRESH_THRESHOLD >= expiryTime) {
      refreshToken().catch(error => {
        logger.error('Failed to refresh token:', error);
      });
    }

    return token;
  } catch {
    return null;
  }
};

export const setToken = (token: string): void => {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) throw new Error('Invalid token format');
    const payload = JSON.parse(atob(parts[1]!)) as TokenPayload;
    if (!payload.exp || !payload.sub) throw new Error('Missing claims');

    const expiry = payload.exp * 1000;
    if (expiry <= Date.now()) throw new Error('Token expired');

    // Read before the writes below overwrite what it compares against.
    const isRotation = hasLiveSessionFor(payload.sub);
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(TOKEN_EXPIRY_KEY, String(expiry));
    localStorage.setItem(USER_EMAIL_KEY, payload.sub);
    localStorage.setItem(REMEMBER_ME_KEY, 'true');
    // A refresh used to broadcast "authenticated" and reload settings, the
    // user, and the persona every few minutes. Only a real sign-in does now.
    if (isRotation) notifyTokenRotated(token);
    else notifyAuthStateChange(true);
  } catch (error) {
    logger.error('Error setting token:', error);

    removeToken();
    throw error;
  }
};

/** Kept for existing callers; the session ends, the store stays. */
export const removeToken = (): void => {
  endSession();
};

export const isAuthenticated = (): boolean => !!getToken();
export const isRememberMeEnabled = (): boolean => localStorage.getItem(REMEMBER_ME_KEY) === 'true';

export const onAuthStateChange = (callback: (isAuthenticated: boolean) => void): (() => void) => {
  const handler = (event: Event): void => {
    const authEvent = event as AuthStateChangeEvent;
    callback(authEvent.detail.isAuthenticated);
  };
  window.addEventListener(AUTH_STATE_CHANGE_EVENT, handler);
  return () => window.removeEventListener(AUTH_STATE_CHANGE_EVENT, handler);
};

/**
 * Subscribe to same-user token replacements (a refresh), receiving the new
 * token. Sign-in and sign-out go to {@link onAuthStateChange} instead; returns
 * an unsubscribe function.
 */
export const onTokenRotated = (callback: (token: string) => void): (() => void) => {
  const handler = (event: Event): void => {
    callback((event as CustomEvent<{ token: string }>).detail.token);
  };
  window.addEventListener(TOKEN_ROTATED_EVENT, handler);
  return () => window.removeEventListener(TOKEN_ROTATED_EVENT, handler);
};

let refreshInFlight: Promise<void> | null = null;

/**
 * Refresh the token, single-flight: every request in the refresh window calls
 * `getToken()`, so a view's fan-out would otherwise send one refresh each.
 * Callers share the in-flight attempt; once it settles a later call may retry.
 */
const refreshToken = (): Promise<void> => {
  refreshInFlight ??= requestRefresh().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
};

const requestRefresh = async (): Promise<void> => {
  // No token is nothing to refresh: sending `Bearer null` would only earn a
  // 401 that ends a session which has already ended.
  const token = localStorage.getItem(TOKEN_KEY);
  if (!token) return;
  try {
    // Intentional bare fetch: the shared api.ts client imports from this module
    // and triggers token refresh on 401, so calling it here would be circular and
    // could recurse. Token refresh must talk to the backend directly.
    const response = await fetch(`${API_URL}/api/auth/refresh`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    setToken(data.access_token);
  } catch (error) {
    logger.error('Error refreshing token:', error);

    // The network being down is not the server rejecting us, so it must not
    // sign the user out mid-flight; the token's own expiry ends the session.
    // A refusal the server did send does end it — without wiping the store,
    // so entries written while offline survive re-login.
    if (!isNetworkError(error)) endSession();
  }
};

// ---------------------------------------------------------------------------
// 🔄  Session-Recovery Helpers
// ---------------------------------------------------------------------------

/**
 * Attempt to recover an authentication token that might be supplied via a
 * query-string or hash fragment in the current URL.
 *
 * This is primarily used after an OAuth redirect where the backend appends the
 * freshly minted JWT as a `token` parameter so the SPA can persist it in
 * `localStorage`.
 *
 * Example callback URLs that this function can handle:
 *   • https://app.example.com/?token=abc.def.ghi
 *   • https://app.example.com/#/callback?token=abc.def.ghi
 *
 * If a valid token is found it is stored via {@link setToken} and the function
 * returns `true`; otherwise it is a no-op that returns `false`.
 */
export const recoverAuth = (): boolean => {
  try {
    const url = new URL(window.location.href);

    // Search both search-params and hash-fragment for a `token` parameter.
    const tokenFromQuery = url.searchParams.get('token');

    // The hash can look like `#/callback?token=...` – parse it if present.
    let tokenFromHash: string | null = null;
    if (url.hash) {
      const hashQueryIndex = url.hash.indexOf('?');
      if (hashQueryIndex > -1) {
        const hashQuery = url.hash.substring(hashQueryIndex + 1);
        const hashParams = new URLSearchParams(hashQuery);
        tokenFromHash = hashParams.get('token');
      }
    }

    const token = tokenFromQuery ?? tokenFromHash;
    if (!token) return false;

    setToken(token);

    // Clean the token from the URL to avoid it lingering in the address bar or
    // browser history. We use the History API so we do not cause a full page
    // reload.
    url.searchParams.delete('token');
    const cleanHash = url.hash.replace(/token=[^&]+&?/, '').replace(/[?&]$/, '');
    const newUrl = `${url.origin}${url.pathname}${url.searchParams.toString() ? `?${url.searchParams}` : ''}${cleanHash}`;
    window.history.replaceState({}, document.title, newUrl);

    return true;
  } catch (error) {
    logger.error('Error during token recovery:', error);

    return false;
  }
};

// ---------------------------------------------------------------------------
// 💻  Browser-Close Detection
// ---------------------------------------------------------------------------

/**
 * Register a `beforeunload` handler that logs the user out when they close the
 * browser *unless* they opted into "remember me".
 *
 * The backend can also make use of the `auth_browser_session` key to invalidate
 * refresh tokens tied to a particular browser session ID.
 */
export const initializeBrowserCloseDetection = (): void => {
  try {
    // Do not register if the user explicitly chose to stay logged-in.
    if (isRememberMeEnabled()) return;

    // Generate a pseudo-random session identifier once per tab.
    if (!sessionStorage.getItem(BROWSER_SESSION_KEY)) {
      sessionStorage.setItem(BROWSER_SESSION_KEY, crypto.randomUUID());
    }

    // Mark that we should logout when the tab/window is closed.
    localStorage.setItem(LOGOUT_ON_BROWSER_CLOSE_KEY, 'true');

    window.addEventListener('beforeunload', () => {
      try {
        // Another tab might have already cleared the flag – check again.
        const shouldLogout = localStorage.getItem(LOGOUT_ON_BROWSER_CLOSE_KEY) === 'true';
        if (shouldLogout) {
          // The user chose not to be remembered, so the store goes too; this
          // handler cannot await IndexedDB, so the next boot does the delete.
          markWipePending();
          endSession();
        }
      } catch {
        // Ignore – we are about to unload anyway.
      }
    });
  } catch (error) {
    logger.error('Browser-close detection initialisation failed:', error);
  }
};

// END FULL AUTH IMPLEMENTATION
